import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
    ClipboardDocumentIcon,
    UserGroupIcon,
    EyeIcon,
    CheckIcon,
    XMarkIcon,
    TrashIcon,
} from '@heroicons/react/24/outline';
import PageHeader from '../../components/common/PageHeader';
import EmptyState from '../../components/common/EmptyState';
import StatusBadge from '../../components/common/StatusBadge';
import { onboardingApi } from '../../features/onboarding/api';

const PENDING = ['submitted', 'pending_review'];
const REJECTED = ['rejected'];

function reviewLabel(status) {
    const map = {
        draft: 'In progress',
        submitted: 'Pending review',
        pending_review: 'Pending review',
        rejected: 'Rejected draft',
        accepted: 'Approved',
        not_started: 'Not started',
    };
    return map[status] || String(status || '—').replace(/_/g, ' ');
}

function DetailRow({ label, value }) {
    if (!value) return null;
    return (
        <div className="py-2 border-b border-border last:border-0">
            <div className="text-xs font-semibold uppercase tracking-wide text-ink-500">{label}</div>
            <div className="text-sm text-ink-900 mt-0.5 whitespace-pre-wrap">{value}</div>
        </div>
    );
}

function OnboardingDetailModal({ userId, onClose, onAction }) {
    const [loading, setLoading] = useState(true);
    const [detail, setDetail] = useState(null);
    const [rejectReason, setRejectReason] = useState('');
    const [busy, setBusy] = useState(null);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const res = await onboardingApi.adminDetail(userId);
            setDetail(res);
        } catch (err) {
            toast.error(err.message || 'Unable to load submission');
            onClose();
        } finally {
            setLoading(false);
        }
    }, [userId, onClose]);

    useEffect(() => {
        load();
    }, [load]);

    const accept = async () => {
        try {
            setBusy('accept');
            await onboardingApi.adminAccept(userId);
            toast.success('Intern approved — they can log in now.');
            onAction();
            onClose();
        } catch (err) {
            toast.error(err.message || 'Unable to approve');
        } finally {
            setBusy(null);
        }
    };

    const reject = async () => {
        try {
            setBusy('reject');
            await onboardingApi.adminReject(userId, rejectReason);
            toast.success('Submission rejected — moved to drafts.');
            onAction();
            onClose();
        } catch (err) {
            toast.error(err.message || 'Unable to reject');
        } finally {
            setBusy(null);
        }
    };

    const removeDraft = async () => {
        if (!window.confirm('Delete this draft permanently? This cannot be undone.')) return;
        try {
            setBusy('delete');
            await onboardingApi.adminDeleteDraft(userId);
            toast.success('Draft deleted.');
            onAction();
            onClose();
        } catch (err) {
            toast.error(err.message || 'Unable to delete draft');
        } finally {
            setBusy(null);
        }
    };

    const preview = detail?.preview;
    const review = detail?.review;
    const user = detail?.user;

    return (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-ink-950/40">
            <div
                className="card w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col animate-fade-up"
                role="dialog"
                aria-modal="true"
                aria-labelledby="onboarding-detail-title"
            >
                <div className="flex items-start justify-between gap-4 border-b border-border pb-4">
                    <div>
                        <h2 id="onboarding-detail-title" className="text-lg font-semibold text-ink-950">
                            {user ? `${user.first_name} ${user.last_name}` : 'Onboarding submission'}
                        </h2>
                        <p className="text-sm text-ink-600 mt-1">{user?.email}</p>
                        {review && (
                            <p className="text-xs text-ink-500 mt-2">
                                Sections filled: {review.sections_filled}/{review.sections_total}
                            </p>
                        )}
                    </div>
                    <button type="button" className="btn-ghost !min-h-[36px]" onClick={onClose}>
                        Close
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto py-4 space-y-4">
                    {loading ? (
                        <div className="space-y-3">
                            <div className="skeleton h-8" />
                            <div className="skeleton h-32" />
                        </div>
                    ) : !preview ? (
                        <p className="text-sm text-ink-600">No onboarding data submitted yet.</p>
                    ) : (
                        <>
                            <DetailRow label="Full name" value={preview.personal?.full_name} />
                            <DetailRow label="Phone" value={preview.contact?.phone} />
                            <DetailRow label="Email" value={preview.contact?.email} />
                            <DetailRow label="City" value={preview.contact?.city} />
                            <DetailRow label="Address" value={preview.contact?.address} />
                            <DetailRow
                                label="Emergency contact"
                                value={
                                    preview.contact?.emergency_name
                                        ? `${preview.contact.emergency_name} (${preview.contact.emergency_relation || '—'}) · ${preview.contact.emergency_phone || ''}`
                                        : null
                                }
                            />
                            <DetailRow
                                label="Education"
                                value={(preview.education || [])
                                    .map((e) => `${e.level} — ${e.institution}`)
                                    .join('\n')}
                            />
                            <DetailRow
                                label="Skills"
                                value={(preview.skills || []).map((s) => s.skill_name).join(', ')}
                            />
                            <DetailRow
                                label="Experience"
                                value={(preview.experiences || [])
                                    .map((e) => `${e.role_title || e.experience_type} at ${e.organization}`)
                                    .join('\n') || 'None / skipped'}
                            />
                            <DetailRow label="AI journey" value={preview.ai_journey?.story} />
                            <DetailRow label="Short-term goals" value={preview.career_goals?.short_term} />
                            <DetailRow label="Long-term goals" value={preview.career_goals?.long_term} />
                            <DetailRow label="Builder mindset" value={preview.builder_mindset?.details} />
                            {review?.rejection_reason && (
                                <div className="rounded-lg bg-red-50 border border-red-100 p-3 text-sm text-red-800">
                                    <strong>Rejection note:</strong> {review.rejection_reason}
                                </div>
                            )}
                        </>
                    )}
                </div>

                {review && !loading && (
                    <div className="border-t border-border pt-4 space-y-3">
                        {review.can_reject && (
                            <textarea
                                className="input w-full min-h-[72px] text-sm"
                                placeholder="Optional note for the intern if you reject…"
                                value={rejectReason}
                                onChange={(e) => setRejectReason(e.target.value)}
                            />
                        )}
                        <div className="flex flex-wrap gap-2 justify-end">
                            {review.can_delete && (
                                <button
                                    type="button"
                                    className="btn-secondary !text-red-700"
                                    disabled={!!busy}
                                    onClick={removeDraft}
                                >
                                    <TrashIcon className="h-4 w-4" />
                                    Delete draft
                                </button>
                            )}
                            {review.can_reject && (
                                <button
                                    type="button"
                                    className="btn-secondary"
                                    disabled={!!busy}
                                    onClick={reject}
                                >
                                    <XMarkIcon className="h-4 w-4" />
                                    Reject
                                </button>
                            )}
                            {review.can_accept && (
                                <button
                                    type="button"
                                    className="btn-primary"
                                    disabled={!!busy}
                                    onClick={accept}
                                >
                                    <CheckIcon className="h-4 w-4" />
                                    Accept & enable login
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function ShareOnboardingLinkCard() {
    const [publicLink, setPublicLink] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        onboardingApi.adminPublicInviteLink()
            .then((res) => setPublicLink(res.link || `${window.location.origin}/onboarding`))
            .catch(() => setPublicLink(`${window.location.origin}/onboarding`));
    }, []);

    const copyText = async (text, message) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success(message);
        } catch {
            toast.error('Could not copy — please select and copy the link manually');
        }
    };

    const copyPublicLink = () => copyText(publicLink, 'Onboarding link copied — share with any student');

    const generateAndCopyUniqueLink = async () => {
        try {
            setBusy(true);
            const res = await onboardingApi.adminCreateInviteLink();
            const link = res.link;
            if (!link) {
                toast.error('No link returned');
                return;
            }
            await copyText(link, 'Personal onboarding link copied for one student');
        } catch (err) {
            toast.error(err.message || 'Unable to create link');
        } finally {
            setBusy(false);
        }
    };

    return (
        <section className="card border-brand-200 bg-gradient-to-br from-brand-50/80 to-white space-y-4">
            <div>
                <h3 className="text-base font-semibold text-ink-950">Share onboarding form</h3>
                <p className="text-sm text-ink-600 mt-1 max-w-2xl">
                    Copy this link and send it to any student via WhatsApp, email, or SMS.
                    Each student opens their own private talent profile form.
                </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
                <input
                    type="text"
                    readOnly
                    value={publicLink}
                    className="input flex-1 font-mono text-xs sm:text-sm bg-white"
                    aria-label="Onboarding link for students"
                    onFocus={(e) => e.target.select()}
                />
                <button
                    type="button"
                    className="btn-primary shrink-0"
                    onClick={copyPublicLink}
                    disabled={!publicLink}
                >
                    <ClipboardDocumentIcon className="h-5 w-5" />
                    Copy link
                </button>
            </div>
            <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-brand-100">
                <span className="text-xs text-ink-500">Need a separate link for one student?</span>
                <button
                    type="button"
                    className="btn-secondary !min-h-[36px] !text-xs"
                    disabled={busy}
                    onClick={generateAndCopyUniqueLink}
                >
                    {busy ? 'Creating…' : 'Generate unique link'}
                </button>
            </div>
        </section>
    );
}

function InternTable({ rows, linkBusy, onCopyLink, onView, emptyTitle, emptyDescription }) {
    if (rows.length === 0) {
        return (
            <EmptyState
                icon={UserGroupIcon}
                title={emptyTitle}
                description={emptyDescription}
            />
        );
    }

    return (
        <div className="card !p-0 overflow-hidden">
            <table className="min-w-full text-sm">
                <thead className="bg-ink-50 text-left text-xs uppercase tracking-wider text-ink-500 border-b border-border">
                    <tr>
                        <th className="px-4 py-3 font-semibold">Intern</th>
                        <th className="px-4 py-3 font-semibold">Email</th>
                        <th className="px-4 py-3 font-semibold">Sections</th>
                        <th className="px-4 py-3 font-semibold">Status</th>
                        <th className="px-4 py-3 font-semibold">Actions</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-border">
                    {rows.map((row) => {
                        const name = row.name || 'Intern';
                        const status = row.draft_status || row.onboarding_status || 'not_started';
                        const rowKey = row.user_id || `draft-${row.draft_id || row.draft_token}`;
                        const linkKey = row.user_id || row.draft_token;
                        return (
                            <tr key={rowKey} className="hover:bg-ink-50/80">
                                <td className="px-4 py-3 font-medium text-ink-950">
                                    {name}
                                    {row.is_anonymous && (
                                        <span className="ml-2 text-[10px] font-semibold uppercase text-ink-400">
                                            Applicant
                                        </span>
                                    )}
                                </td>
                                <td className="px-4 py-3 text-ink-600">{row.email}</td>
                                <td className="px-4 py-3 tabular-nums">
                                    {row.sections_filled != null
                                        ? `${row.sections_filled}/${row.sections_total}`
                                        : '—'}
                                </td>
                                <td className="px-4 py-3">
                                    <StatusBadge
                                        status={
                                            status === 'accepted'
                                                ? 'approved'
                                                : PENDING.includes(status)
                                                    ? 'pending'
                                                    : status === 'rejected'
                                                        ? 'rejected'
                                                        : status === 'draft'
                                                            ? 'in_progress'
                                                            : status
                                        }
                                        label={reviewLabel(status)}
                                    />
                                </td>
                                <td className="px-4 py-3">
                                    <div className="flex flex-wrap gap-2">
                                        <button
                                            type="button"
                                            className="btn-secondary !min-h-[36px] !px-3 !py-1.5 !text-xs"
                                            disabled={linkBusy === linkKey}
                                            onClick={() => onCopyLink(row)}
                                        >
                                            <ClipboardDocumentIcon className="h-4 w-4" />
                                            Copy link
                                        </button>
                                        {(PENDING.includes(status) || status === 'rejected' || status === 'accepted') && row.user_id && (
                                            <button
                                                type="button"
                                                className="btn-secondary !min-h-[36px] !px-3 !py-1.5 !text-xs"
                                                onClick={() => onView(row.user_id)}
                                            >
                                                <EyeIcon className="h-4 w-4" />
                                                Review
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

export default function AdminOnboarding() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [linkBusy, setLinkBusy] = useState(null);
    const [viewUserId, setViewUserId] = useState(null);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const res = await onboardingApi.adminInterns();
            setRows(res.interns || []);
        } catch (err) {
            toast.error(err.message || 'Unable to load onboarding records');
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const pendingRows = useMemo(
        () => rows.filter((r) => PENDING.includes(r.draft_status)),
        [rows]
    );
    const rejectedRows = useMemo(
        () => rows.filter((r) => REJECTED.includes(r.draft_status)),
        [rows]
    );
    const otherRows = useMemo(
        () => rows.filter((r) => !PENDING.includes(r.draft_status) && !REJECTED.includes(r.draft_status)),
        [rows]
    );

    const copyLink = async (row) => {
        const linkKey = row.user_id || row.draft_token;
        const name = row.name || 'student';
        try {
            setLinkBusy(linkKey);
            let link;
            if (row.user_id) {
                const res = await onboardingApi.adminLink(row.user_id);
                link = res.link || res.data?.link;
            } else if (row.draft_token) {
                link = `${window.location.origin}/onboarding?resume=${encodeURIComponent(row.draft_token)}`;
            } else {
                const res = await onboardingApi.adminCreateInviteLink();
                link = res.link;
            }
            if (!link) {
                toast.error('No link returned');
                return;
            }
            await navigator.clipboard.writeText(link);
            toast.success(`Onboarding link copied for ${name}`);
        } catch (err) {
            toast.error(err.message || 'Unable to create link');
        } finally {
            setLinkBusy(null);
        }
    };

    return (
        <div className="w-full space-y-8">
            <PageHeader
                eyebrow="Program management"
                title="Talent profile onboarding"
                description="Share onboarding links with interns, review submissions, approve accounts for login, or send back to draft."
            />

            <ShareOnboardingLinkCard />

            {loading ? (
                <div className="space-y-3" role="status">
                    <div className="skeleton h-12" />
                    <div className="skeleton h-48" />
                </div>
            ) : (
                <>
                    <section className="space-y-3">
                        <h3 className="text-base font-semibold text-ink-950">
                            Pending review ({pendingRows.length})
                        </h3>
                        <InternTable
                            rows={pendingRows}
                            linkBusy={linkBusy}
                            onCopyLink={copyLink}
                            onView={setViewUserId}
                            emptyTitle="No submissions awaiting review"
                            emptyDescription="When interns submit their talent profile, they appear here for you to verify."
                        />
                    </section>

                    <section className="space-y-3">
                        <h3 className="text-base font-semibold text-ink-950">All interns</h3>
                        <InternTable
                            rows={otherRows}
                            linkBusy={linkBusy}
                            onCopyLink={copyLink}
                            onView={setViewUserId}
                            emptyTitle="No intern onboarding records yet"
                            emptyDescription="Interns appear here once they have a profile in the system."
                        />
                    </section>

                    <section className="space-y-3">
                        <h3 className="text-base font-semibold text-ink-950">
                            Rejected drafts ({rejectedRows.length})
                        </h3>
                        <p className="text-sm text-ink-600">
                            Rejected submissions move here. Delete the draft or accept after the intern revises via their link.
                        </p>
                        <InternTable
                            rows={rejectedRows}
                            linkBusy={linkBusy}
                            onCopyLink={copyLink}
                            onView={setViewUserId}
                            emptyTitle="No rejected drafts"
                            emptyDescription="Rejected submissions will appear here for delete or re-approval."
                        />
                    </section>
                </>
            )}

            {viewUserId && (
                <OnboardingDetailModal
                    userId={viewUserId}
                    onClose={() => setViewUserId(null)}
                    onAction={load}
                />
            )}
        </div>
    );
}
